/**
 * Deadline + inspection-freshness lenses for the Officer Portal dashboard.
 *
 * Statutory deadline *derivation* now lives server-side: gov-api's
 * `GET /deadlines` aggregates every active clock across the open caseload using
 * the shared `@munifocus/domain-deadlines` rules, so the dashboard, the reminder
 * emails, and the escalation step screens can never disagree. `useDeadlines`
 * simply reads that endpoint — no more per-file fan-out.
 *
 * Inspection freshness (X4) is still derived client-side from evidence capture
 * dates; it is a read-only lens with no server counterpart yet.
 */
import { useMemo } from 'react';
import { useQueries, useQuery } from '@tanstack/react-query';
import type { Evidence, FileSummary } from '@munifocus/contracts-gov';
import { clockFromDeadline, type DeadlineItem } from '@munifocus/domain-deadlines';
import { api } from './api-client';
import { useFiles } from './files-api';
import { evidenceKeys, listEvidence } from './evidence-api';

// Re-export the shared derivation + types so existing importers keep their path.
export {
  deriveDeadlines,
  deriveFileDeadlines,
  type DeadlineItem,
  type DeadlineKind,
} from '@munifocus/domain-deadlines';

// --- deadlines (server-aggregated) -----------------------------------------

import { deadlinesKeys } from './deadlines-keys';

export { deadlinesKeys };

export async function listDeadlines(): Promise<DeadlineItem[]> {
  const { data } = await api.get<DeadlineItem[]>('/deadlines');
  return data;
}

/**
 * Every active statutory clock across the officer's open files, soonest (most
 * overdue) first. Aggregated server-side; this is a single cached read.
 */
export function useDeadlines() {
  const query = useQuery({
    queryKey: deadlinesKeys.all,
    queryFn: listDeadlines,
  });

  return {
    data: query.data ?? [],
    isLoading: query.isLoading,
    isError: query.isError,
  };
}

// --- inspection freshness (X4) ---------------------------------------------

/** Days an inspection can age before it stops being treated as current. */
export const INSPECTION_FRESH_DAYS = 30;
/** Beyond this an inspection is stale — courts expect a recent site view. */
export const INSPECTION_AGING_DAYS = 90;

export type FreshnessState = 'fresh' | 'aging' | 'stale' | 'none';

export interface FileFreshness {
  fileId: string;
  reference: string;
  address: string;
  /** Capture date of the most recent piece of evidence, or null if none yet. */
  lastInspectedIso: string | null;
  /** Whole days since that capture; null when there is no inspection on file. */
  daysSince: number | null;
  state: FreshnessState;
}

function freshnessState(daysSince: number): Exclude<FreshnessState, 'none'> {
  if (daysSince <= INSPECTION_FRESH_DAYS) return 'fresh';
  if (daysSince <= INSPECTION_AGING_DAYS) return 'aging';
  return 'stale';
}

/**
 * How fresh a file's site inspection is, taken from the most recent evidence
 * capture (`capturedAt`). A file with no evidence yet is `none` — the officer
 * hasn't been on site.
 */
export function deriveFreshness(
  file: FileSummary,
  evidence: Evidence[],
  now: Date = new Date(),
): FileFreshness {
  const latest = evidence.reduce<string | null>(
    (max, e) => (max == null || e.capturedAt > max ? e.capturedAt : max),
    null,
  );

  if (!latest) {
    return {
      fileId: file.id,
      reference: file.reference,
      address: file.address,
      lastInspectedIso: null,
      daysSince: null,
      state: 'none',
    };
  }

  const clock = clockFromDeadline(latest, now);
  const daysSince = clock ? -clock.daysRemaining : 0;
  return {
    fileId: file.id,
    reference: file.reference,
    address: file.address,
    lastInspectedIso: latest,
    daysSince,
    state: freshnessState(daysSince),
  };
}

export interface FreshnessSummary {
  /** Files for which a freshness judgement was made (every open file checked). */
  tracked: number;
  fresh: number;
  aging: number;
  stale: number;
  none: number;
  /** The most overdue inspection (largest `daysSince`), for a deep-link nudge. */
  stalest: FileFreshness | null;
}

/** Sort key for "worst offender" — a never-inspected file outranks any aged one. */
function stalenessRank(item: FileFreshness | null): number {
  if (item == null) return -1;
  return item.state === 'none' ? Number.POSITIVE_INFINITY : (item.daysSince ?? 0);
}

export function summarizeFreshness(items: FileFreshness[]): FreshnessSummary {
  const summary: FreshnessSummary = {
    tracked: items.length,
    fresh: 0,
    aging: 0,
    stale: 0,
    none: 0,
    stalest: null,
  };
  for (const item of items) {
    summary[item.state] += 1;
    if (stalenessRank(item) > stalenessRank(summary.stalest)) summary.stalest = item;
  }
  return summary;
}

// --- freshness hook --------------------------------------------------------

/** A file is "open" — and so worth tracking — until it reaches a closed state. */
function isOpenFile(f: FileSummary): boolean {
  return f.stage !== 'closed' && f.status !== 'closed';
}

/**
 * Inspection freshness across the officer's open files. Fans out evidence per
 * file (reusing the evidence query key) and reduces each to its most recent
 * capture, then summarises for the dashboard indicator.
 */
export function useInspectionFreshness() {
  const filesQuery = useFiles();
  const openFiles = useMemo(
    () => (filesQuery.data?.items ?? []).filter(isOpenFile),
    [filesQuery.data],
  );

  const evidenceQueries = useQueries({
    queries: openFiles.map((f) => ({
      queryKey: evidenceKeys.forFile(f.id),
      queryFn: () => listEvidence(f.id),
    })),
  });

  const items = openFiles.map((f, i) => deriveFreshness(f, evidenceQueries[i]?.data ?? []));

  const summary = useMemo(() => summarizeFreshness(items), [JSON.stringify(items)]);

  return {
    data: summary,
    isLoading: filesQuery.isLoading || evidenceQueries.some((q) => q.isLoading),
    isError: filesQuery.isError || evidenceQueries.some((q) => q.isError),
  };
}
